<template>
  <!-- Card wrapper to host toolbar and chart -->
  <!-- Reduced top padding (pt-2) to pull toolbar closer to the top border -->
  <div
    class="relative rounded-xl backdrop-blur w-full"
    style="background-color:#2b2b34; padding: 0.5rem 1rem 1rem 1rem; display: grid; grid-template-rows: auto 1fr; grid-template-columns: 1fr minmax(max-content, 360px); gap: 0;"
  >
    <!-- Toolbar full-width row aligned right -->
    <!-- Toolbar placed in grid column 2 row 1 (top-right) -->
    <div
      style="grid-column: 2 / 3; grid-row: 1 / 2;"
      class="flex flex-nowrap justify-end items-center space-x-2"
    >
      <!-- Mode buttons -->
      <template v-for="mode in modes" :key="mode.value">
        <button
          @click="selectedMode = mode.value"
          :disabled="selectedMode === mode.value"
          :class="['tf-btn', { selected: selectedMode === mode.value }]"
        >
          {{ mode.label }}
        </button>
      </template>

      <!-- Range buttons -->
      <template v-for="(r, idx) in ranges" :key="r.value">
        <button
          @click="selectedRange = r.value"
          :disabled="selectedRange === r.value"
          :class="['tf-btn', { selected: selectedRange === r.value, 'range-first': idx === 0 }]"
        >
          {{ r.label }}
        </button>
      </template>
    </div>

    <!-- Forecast chart -->
    <!-- Chart spans full width under toolbar -->
    <DxChart
      id="tank-forecast-chart"
      :data-source="chartData"
      :size="{ width: undefined }"
      style="grid-column: 1 / 3; grid-row: 2 / 3;"
    >
      <DxTitle :text="titleText" />

      <DxArgumentAxis argument-type="datetime" />

      <!-- Historical actuals -->
      <DxSeries
        value-field="history"
        argument-field="date"
        name="History"
        type="line"
        color="#1f78b4"
        :pane="undefined"
        :point="{ visible: true, size: 8 }"
      />

      <!-- Forecast central estimate -->
      <DxSeries
        value-field="forecast"
        argument-field="date"
        name="Forecast"
        type="line"
        dash-style="dash"
        color="#33a02c"
        :point="{ visible: true, size: 8 }"
      />

      <!-- Optional uncertainty band via rangeArea – DevExtreme supports it but
           in case of version mismatch you can comment this block. -->
      <!--
      <DxSeries
        argument-field="date"
        range-value1-field="low"
        range-value2-field="high"
        name="CI"
        type="rangeArea"
        color="#33a02c33"
        :opacity="0.25"
        :point-visible="false"
        :show-in-legend="false"
      />
      -->

      <DxLegend vertical-alignment="bottom" horizontal-alignment="center" />

      <DxTooltip :enabled="true" :customize-tooltip="customizeTooltip" />

      <!-- Crosshair to help users align hover with dates -->
      <DxCrosshair :enabled="true" />
    </DxChart>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import { DxChart, DxSeries, DxTitle, DxLegend, DxTooltip, DxArgumentAxis, DxCrosshair } from 'devextreme-vue/chart';

// --- external state & data helpers ---------------------------------------
import filtersState from './temp-data-and-supports/useForecastFilters.js';
import { useForecast } from './temp-data-and-supports/useForecastApi.js';
// Shared view state (range + mode)
import { selectedRange, selectedMode } from './temp-data-and-supports/useForecastViewState.js';

function customizeTooltip(info) {
  // `info.value` and `info.argument` are always defined for visible points
  const isLevel = selectedMode.value === 'level';
  const unit = isLevel ? 'L' : 'L/day';

  const dateLabel = new Date(info.argument).toLocaleDateString();
  const val = info.value;

  // Build label based on series name so we don’t depend on data structure
  let label;
  if (info.seriesName === 'History') {
    label = `Actual: ${val.toFixed(isLevel ? 0 : 3)} ${unit}`;
  } else {
    label = `Forecast: ${val.toFixed(isLevel ? 0 : 3)} ${unit}`;
  }

  return {
    text: `${dateLabel}: ${label}`,
  };
}

// -------- toolbar state --------
// selectedRange / selectedMode now live in a shared composable so other
// components (e.g. MetricsCards) can stay in sync.

// Range selector items – value in days, label shown on button
const ranges = [
  { value: 30,  label: '30' },
  { value: 60,  label: '60' },
  { value: 90,  label: '90' },
  { value: 183, label: '1/2 year' },
  { value: 365, label: '1 year' },
];
const modes  = [
  { value: 'level', label: 'Level Forecast' },
  { value: 'usage', label: 'Usage Forecast' },
];

// Dynamic title & series label
const titleText = computed(() =>
  selectedMode.value === 'level' ? 'Tank Level Forecast' : 'Tank Usage Forecast'
);

// Legend / series label at bottom of chart


// ----------------- dynamic chart data ------------------------------------

// Computed data-source for the chart – dynamically derived from current
// selections, mode and horizon.
const { loading, data, error, load } = useForecast();

// Kick-off API call whenever Operator or Chemical changes
// Kick-off API call whenever Operator, Chemical, or Scope changes
watch(
  () => [filtersState.operatorId, filtersState.chemical, filtersState.selectedScope],
  ([op, chem, scope]) => {
    if (op && chem) {
      const scopeType = scope.type === 'operator' ? null : scope.type;
      const scopeId = scope.id;
      // load(operatorId, chemical, horizonDays, scopeType, scopeId)
      load(op, chem, 365, scopeType, scopeId);
    }
  },
  { immediate: true }
);

const chartData = computed(() => {
  if (!data.value) return [];

  // pick correct metric branch (level vs consumption)
  const metricKey = selectedMode.value === 'level' ? 'level' : 'consumption';
  // Some back-end payloads were observed to nest history under slightly
  // different keys when the model switches.  Primary path remains
  //   data[metricKey].history  (e.g. data.level.history)
  // but fall back to a top-level `history` array if present.

  let histArr = data.value?.[metricKey]?.history;
  if (!Array.isArray(histArr)) {
    histArr = Array.isArray(data.value?.history) ? data.value.history : [];
  }
  let fcArr = data.value?.[metricKey]?.forecast;
  if (!Array.isArray(fcArr)) {
    fcArr = Array.isArray(data.value?.forecast) ? data.value.forecast : [];
  }

  // guard – without forecast nothing to plot
  if (!Array.isArray(histArr) && !Array.isArray(fcArr)) return [];

  const rows = [];

  const capHist = histArr.slice(-selectedRange.value); // keep last N days if longer
  capHist.forEach(p => {
    rows.push({
      date: new Date(p.ds),
      history: p.y,
      forecast: null,
      low: null,
      high: null,
    });
  });

  const capFc = fcArr.slice(0, selectedRange.value);

  // Bridge: ensure forecast series starts at last history point so the dashed
  // line connects visually.
  if (capHist.length && capFc.length) {
    const lastHist = capHist[capHist.length - 1];
    const firstFcDate = new Date(capFc[0].ds);
    const lastHistDate = new Date(lastHist.ds);

    if (firstFcDate > lastHistDate) {
      capFc.unshift({
        ds: lastHist.ds,
        yhat: lastHist.y,
        yhat_lower: lastHist.y,
        yhat_upper: lastHist.y,
      });
    }
  }
  capFc.forEach(p => {
    rows.push({
      date: new Date(p.ds),
      history: null,
      forecast: p.yhat,
      low: p.yhat_lower ?? null,
      high: p.yhat_upper ?? null,
    });
  });

  // Ensure chronological order
  return rows.sort((a, b) => a.date - b.date);
});

// Button appearance helper
// no longer used – class styling via CSS
</script>

<style scoped>
#tank-forecast-chart {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
}

/* toolbar buttons */
.tf-btn {
  /* Slightly increased vertical padding to make the buttons a bit taller */
  padding: 8px 12px;
  font-size: 0.875rem;
  border-radius: 0.25rem;
  background: #2b2b34;
  color: #d1d5db;
  border: 1px solid #9ca3af;
}
.tf-btn:hover:not(.selected):not([disabled]) {
  background: #3f3f46;
}
.tf-btn.selected,
.tf-btn:disabled {
  background: #f4711f;
  color: #ffffff;
  border: none;
  cursor: default;
}

/* uniform spacing between buttons */
.tf-btn:not(:first-child) {
  margin-left: 0.5rem;
}

.range-first {
  /* Provide a little extra spacing between the last mode button and the first range button */
  margin-left: 2rem;
}
</style>
